import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { PhoneInput, combinePhone } from "@/components/ui/PhoneInput";
import { toast } from "sonner";
import { HeartHandshake, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function SignupParent() {
  const { signupParent } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState({ country: "IN", number: "" });
  const [form, setForm] = useState({ full_name: "", email: "", password: "", child_email: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await signupParent({ ...form, phone: combinePhone(phone) });
      toast.success(res.linked_children ? `Account created · ${res.linked_children} child linked` : "Account created — link your child after sign-in");
      nav("/parent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[440px] h-[440px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.30), transparent 60%)" }} />
          <div className="absolute -bottom-40 -right-16 w-[460px] h-[460px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.20), transparent 60%)" }} />
        </div>
        <div className="relative w-full max-w-md mx-auto px-6 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <HeartHandshake size={12} /> Parent signup
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Stay close to your child's progress.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">Attendance, upcoming classes, and a weekly digest — in one calm place.</p>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
            className="bg-white border border-black/8 rounded-2xl shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)] mt-8 p-6 sm:p-8 space-y-4"
          >
            <GlowField label="Your full name" testid="parent-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            <GlowField label="Your email" testid="parent-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
            <GlowField label="Password" testid="parent-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
            <div>
              <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Phone (optional)</div>
              <PhoneInput value={phone} onChange={setPhone} testid="parent-phone" />
            </div>
            <GlowField label="Child's student email (optional)" testid="parent-child-email" type="email" value={form.child_email} onChange={(e) => set("child_email", e.target.value)} hint="If your child already has a student account, link them now. You can add more children later." />
            <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="parent-submit">
              <Sparkles size={14} /> {loading ? "Creating…" : "Create parent account"}
            </CtaButton>
          </motion.form>

          <div className="mt-6 text-sm text-neutral-600">Already have an account? <Link to="/login" className="text-[#0A0C10] font-semibold underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Sign in</Link></div>
        </div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
